import { Show, useModal } from "@refinedev/antd";
import { Tag, Space, Button, Input } from "antd";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import {
  UserOutlined,
  BankOutlined,
  TeamOutlined,
  RocketOutlined,
} from "@ant-design/icons";

import { IContact } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
} from "components/show";

export const ContactShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IContact>();
  const record = result;

  const {
    modalProps: modalPropsShipco,
    show: showShipco,
    close: closeShipco,
  } = useModal();

  const {
    modalProps: modalPropsShipcoGroup,
    show: showShipcoGroup,
    close: closeShipcoGroup,
  } = useModal();

  const {
    modalProps: modalPropsVessel,
    show: showVessel,
    close: closeVessel,
  } = useModal();

  const hasShipco = !!record?.shipco;
  const hasShipcoGroup = !!record?.shipcoGroup;
  const hasVessel = !!record?.vessel;

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "active contact",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Space>
          <Button
            type="default"
            onClick={showShipco}
            disabled={!hasShipco}
          >
            Shipco Details
          </Button>
          <Button
            type="default"
            onClick={showShipcoGroup}
            disabled={!hasShipcoGroup}
          >
            Shipco Group Details
          </Button>
          <Button
            type="default"
            onClick={showVessel}
            disabled={!hasVessel}
          >
            Vessel Details
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Contact Information */}
        <ShowSection
          title="Contact Information"
          subtitle="Personal contact details"
          icon={<UserOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Contact Name" value={record?.name} />
            <ShowField label="Email" value={record?.email} type="email" />
            <ShowField label="Phone" value={record?.phone} type="phone" />
          </ShowFieldGrid>
        </ShowSection>

        {/* Associated Entity */}
        <ShowSection
          title="Associated Entity"
          subtitle="Related business entity"
          icon={<BankOutlined />}
          variant="default"
        >
          <ShowFieldGrid columns={3}>
            {hasShipco && (
              <ShowField label="Shipco" value={record?.shipco?.registrationName} />
            )}
            {hasShipcoGroup && (
              <ShowField label="Shipco Group" value={record?.shipcoGroup?.registrationName} />
            )}
            {hasVessel && (
              <ShowField label="Vessel" value={record?.vessel?.name} />
            )}
          </ShowFieldGrid>
        </ShowSection>
      </Space>

      {/* Shipco Modal */}
      <ShowModal
        title="Shipco Details"
        subtitle={record?.shipco?.registrationName}
        icon={<BankOutlined />}
        open={modalPropsShipco.open ?? false}
        onClose={closeShipco}
        empty={!hasShipco}
        emptyText="No shipco associated with this contact"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Registration Name" value={record?.shipco?.registrationName} />
          <ShowField label="Registration Number" value={record?.shipco?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.shipco?.gstNumber} type="code" copyable />
          <ShowField label="Accreditation Number" value={record?.shipco?.accreditationNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.shipco?.phone} type="phone" />
          <ShowField label="Communication Email" value={record?.shipco?.email} type="email" />
        </ShowFieldGrid>
      </ShowModal>

      {/* Shipco Group Modal */}
      <ShowModal
        title="Shipco Group Details"
        subtitle={record?.shipcoGroup?.registrationName}
        icon={<TeamOutlined />}
        open={modalPropsShipcoGroup.open ?? false}
        onClose={closeShipcoGroup}
        empty={!hasShipcoGroup}
        emptyText="No shipco group associated with this contact"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Registration Name" value={record?.shipcoGroup?.registrationName} />
          <ShowField label="Registration Number" value={record?.shipcoGroup?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.shipcoGroup?.gstNumber} type="code" copyable />
          <ShowField label="Accreditation Number" value={record?.shipcoGroup?.accreditationNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.shipcoGroup?.phone} type="phone" />
          <ShowField label="Communication Email" value={record?.shipcoGroup?.email} type="email" />
        </ShowFieldGrid>
      </ShowModal>

      {/* Vessel Modal */}
      <ShowModal
        title="Vessel Details"
        subtitle={record?.vessel?.name}
        icon={<RocketOutlined />}
        open={modalPropsVessel.open ?? false}
        onClose={closeVessel}
        empty={!hasVessel}
        emptyText="No vessel associated with this contact"
        width={700}
      >
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="Vessel Name" value={record?.vessel?.name} />
            <ShowField label="Vessel Type" value={record?.vessel?.type} type="tag" tagColor="blue" />
            <ShowField label="UKHO Licence" value={record?.vessel?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="IMO Number" value={record?.vessel?.imoNumber} type="code" copyable />
            <ShowField label="Communication Email" value={record?.email} type="email" />
            <ShowField label="Call Sign" value={record?.vessel?.callSign} type="code" />
            <ShowField label="Max Email Size" value={record?.vessel?.maxEmailSizeKb ? prettyBytes(Number(record.vessel.maxEmailSizeKb)) : null} />
            <ShowField label="Max Transmission Size" value={record?.vessel?.maxTransmissionSizeMb} type="number" suffix=" MB" />
            <ShowField label="Status" value={record?.vessel?.holdStatus} type="status" />
          </ShowFieldGrid>
          {record?.vessel?.notes && (
            <div>
              <ShowField label="Notes" value="" />
              <Input.TextArea rows={4} readOnly value={record?.vessel?.notes} style={{ marginTop: 8 }} />
            </div>
          )}
        </Space>
      </ShowModal>
    </Show>
  );
};
